import { useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PageHero from '@/components/PageHero';
import { postEnquiry } from '@/lib/api';

const TYPES = [
    ['general', 'General'],
    ['artwork', 'Available Works'],
    ['private_viewing', 'Private Viewing'],
];

export default function Contact() {
    useDocumentMeta({
        title: 'Contact · Maison Touré',
        description: 'Contact Maison Touré, Sydney — for enquiries, private viewings, and acquisitions.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const c = settings?.contact || {};
    const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', type: 'general' });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await postEnquiry(form); setSent(true); } finally { setBusy(false); }
    };

    return (
        <div ref={reveal} data-testid="contact-page" style={{ paddingTop: 144 }}>
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12 pb-40 md:pb-56">
                <PageHero
                    testid="contact-hero"
                    overline={c.hero_overline ?? 'Get in Touch'}
                    title={c.hero_title ?? 'Contact.'}
                    intro={c.hero_intro ?? ''}
                />

                <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
                    <aside className="md:col-span-4 reveal">
                        <div className="flex flex-col gap-12">
                            <div>
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>Visit</span>
                                <p
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        lineHeight: 1.7,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {c.address_line_1 || 'Maison Touré'}<br />
                                    {c.address_line_2 || 'Sydney, Australia'}<br />
                                    <em className="serif-italic">{c.address_line_3 || 'Private viewings by appointment.'}</em>
                                </p>
                            </div>
                            <div>
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>Write</span>
                                <p
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        lineHeight: 1.8,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {c.email_primary && <><a href={`mailto:${c.email_primary}`} className="hover:text-[var(--ink)]">{c.email_primary}</a><br /></>}
                                    {c.email_secondary && <a href={`mailto:${c.email_secondary}`} className="hover:text-[var(--ink)]">{c.email_secondary}</a>}
                                </p>
                            </div>
                            <div>
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>Call</span>
                                <p
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    <a href={`tel:${(c.phone || '').replace(/\s+/g, '')}`} className="hover:text-[var(--ink)]">{c.phone || ''}</a>
                                </p>
                            </div>
                            <div>
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>Follow</span>
                                <p
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    <a href={c.instagram_url || '#'} target="_blank" rel="noreferrer" className="hover:text-[var(--ink)]">{c.instagram_handle || ''}</a>
                                </p>
                            </div>
                        </div>
                    </aside>

                    <div className="md:col-span-7 md:col-start-6 reveal">
                        {sent ? (
                            <div data-testid="contact-success" className="py-16">
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>Received</span>
                                <h2
                                    className="mt-6"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 'clamp(36px, 4.4vw, 64px)',
                                        lineHeight: 1.06,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Your message has been <em className="serif-italic" style={{ fontWeight: 500 }}>received.</em>
                                </h2>
                                <p
                                    className="mt-8 max-w-lg"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 18,
                                        lineHeight: 1.75,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    A confirmation has been logged with the gallery. A member of the team will respond within one business day.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="flex flex-col gap-10" data-testid="contact-form">
                                <div className="flex gap-8 flex-wrap items-end">
                                    {TYPES.map(([v, l]) => {
                                        const active = form.type === v;
                                        return (
                                            <button
                                                type="button"
                                                key={v}
                                                data-testid={`contact-type-${v}`}
                                                onClick={() => setForm({ ...form, type: v })}
                                                className="overline transition-all duration-500"
                                                aria-pressed={active}
                                                style={{
                                                    background: 'transparent',
                                                    color: active ? 'var(--ink)' : 'var(--ink-3)',
                                                    padding: '4px 0 6px 0',
                                                    letterSpacing: '0.32em',
                                                    cursor: 'pointer',
                                                    borderBottom: `1px solid ${active ? 'var(--ink)' : 'transparent'}`,
                                                }}
                                            >
                                                {l}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex flex-col gap-8">
                                    <input data-testid="contact-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line" />
                                    <input data-testid="contact-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-line" />
                                    <input data-testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-line" />
                                    <textarea data-testid="contact-message" required rows={5} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-line" />
                                </div>
                                <button data-testid="contact-submit" disabled={busy} className="btn-primary self-start mt-2">
                                    {busy ? 'Sending' : 'Send message'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
